/**
 * Programmatic GLB builder — engineered fixtures as REVIEWABLE CODE, not
 * opaque binaries (ADR-008 §9: the report-honesty fixture is constructed,
 * committed, and readable). Builds a minimal spec-valid glTF-binary with a
 * skinned triangle, a joint hierarchy (authored names verbatim — reserved
 * characters included), optional non-joint nodes, and animations in any of
 * the three glTF interpolation modes.
 * Depends on: three (matrix math only). Depended on by: adapter tests.
 */
import { Matrix4, Quaternion, Vector3 } from 'three';

export interface GlbNodeSpec {
  /** Authored name, written to the file verbatim (sanitization is the loader's). */
  readonly name: string;
  /** Index into the nodes array, or null for a scene-root node. */
  readonly parent: number | null;
  readonly translation?: readonly [number, number, number];
  readonly rotation?: readonly [number, number, number, number];
  readonly scale?: readonly [number, number, number];
  /** false → plain node (interposed Object3D), not a skin joint. Default true. */
  readonly joint?: boolean;
  /** Post-multiply the bind world by an X-mirror before inverting — an improper IBM. */
  readonly mirrorBind?: boolean;
}

export type GlbInterpolation = 'LINEAR' | 'STEP' | 'CUBICSPLINE';

export interface GlbChannelSpec {
  /** Node index the channel targets, or 'mesh' for the skinned-mesh node (weights). */
  readonly node: number | 'mesh';
  readonly path: 'rotation' | 'translation' | 'scale' | 'weights';
  readonly interpolation?: GlbInterpolation;
  readonly times: readonly number[];
  /**
   * Flat values: stride per key (rotation 4, weights = morph-target count,
   * else 3) — except CUBICSPLINE, which per spec takes
   * in-tangent/value/out-tangent triplets (3 × stride).
   */
  readonly values: readonly number[];
}

export interface GlbSpec {
  readonly generator?: string;
  readonly extensionsUsed?: readonly string[];
  readonly nodes: readonly GlbNodeSpec[];
  /** Named morph targets on the skinned mesh (glTF extras.targetNames convention). */
  readonly morphTargetNames?: readonly string[];
  readonly animations?: ReadonlyArray<{ readonly name: string; readonly channels: readonly GlbChannelSpec[] }>;
  /**
   * Materials on the skinned primitive. A `null` entry is an UNNAMED material
   * — the case the loader mints identity for, and one no corpus specimen
   * actually contains (Tripo names all of them).
   */
  readonly materials?: readonly (string | null)[];
}

interface BufferSlot {
  readonly bytes: Uint8Array;
  readonly componentType: number;
  readonly count: number;
  readonly type: string;
  readonly min?: number[];
  readonly max?: number[];
}

const FLOAT = 5126;
const UBYTE = 5121;

export function buildSkinnedGlb(spec: GlbSpec): ArrayBuffer {
  // Bind-world computation walks parents first: a parent index must exist
  // and precede its child. Filtered node lists with stale positional parent
  // references die HERE, not as an undefined-matrix crash downstream.
  spec.nodes.forEach((n, i) => {
    if (n.parent !== null && (n.parent < 0 || n.parent >= i)) {
      throw new RangeError(
        `buildSkinnedGlb: node ${i} ("${n.name}") has parent index ${n.parent} — parents must precede children`,
      );
    }
  });
  const slots: BufferSlot[] = [];
  const accessor = (slot: BufferSlot): number => slots.push(slot) - 1;
  const floats = (data: readonly number[], type: string, minMax = false): number => {
    const array = new Float32Array(data);
    const stride = { SCALAR: 1, VEC3: 3, VEC4: 4, MAT4: 16 }[type] as number;
    const slot: BufferSlot = {
      bytes: new Uint8Array(array.buffer),
      componentType: FLOAT,
      count: data.length / stride,
      type,
      ...(minMax
        ? {
            min: Array.from({ length: stride }, (_, k) =>
              Math.min(...data.filter((_, i) => i % stride === k)),
            ),
            max: Array.from({ length: stride }, (_, k) =>
              Math.max(...data.filter((_, i) => i % stride === k)),
            ),
          }
        : {}),
    };
    return accessor(slot);
  };

  // ---- Bind worlds from the node TRS chain; IBMs are their inverses.
  const locals = spec.nodes.map((n) =>
    new Matrix4().compose(
      new Vector3(...(n.translation ?? [0, 0, 0])),
      new Quaternion(...(n.rotation ?? [0, 0, 0, 1])),
      new Vector3(...(n.scale ?? [1, 1, 1])),
    ),
  );
  const worlds: Matrix4[] = [];
  spec.nodes.forEach((n, i) => {
    const local = locals[i] as Matrix4;
    worlds[i] = n.parent === null ? local.clone() : (worlds[n.parent] as Matrix4).clone().multiply(local);
  });
  const jointIndices = spec.nodes.flatMap((n, i) => (n.joint === false ? [] : [i]));
  const ibmData = jointIndices.flatMap((i) => {
    const world = (worlds[i] as Matrix4).clone();
    if (spec.nodes[i]?.mirrorBind) world.multiply(new Matrix4().makeScale(-1, 1, 1));
    return [...world.invert().elements];
  });

  // ---- Minimal skinned triangle, fully weighted to joint 0.
  const positionAccessor = floats([0, 0, 0, 1, 0, 0, 0, 1, 0], 'VEC3', true);
  const jointsAccessor = accessor({
    bytes: new Uint8Array(12).fill(0),
    componentType: UBYTE,
    count: 3,
    type: 'VEC4',
  });
  const weightsAccessor = floats([1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0], 'VEC4');
  const ibmAccessor = floats(ibmData, 'MAT4');

  // ---- Animations (channels may target the mesh node, resolved below).
  const meshNodePlaceholder = spec.nodes.length;
  const animations = (spec.animations ?? []).map((anim) => {
    const samplers: object[] = [];
    const channels: object[] = [];
    for (const ch of anim.channels) {
      const input = floats([...ch.times], 'SCALAR', true);
      const outputType = ch.path === 'rotation' ? 'VEC4' : ch.path === 'weights' ? 'SCALAR' : 'VEC3';
      const output = floats([...ch.values], outputType);
      channels.push({
        sampler: samplers.length,
        target: { node: ch.node === 'mesh' ? meshNodePlaceholder : ch.node, path: ch.path },
      });
      samplers.push({ input, output, interpolation: ch.interpolation ?? 'LINEAR' });
    }
    return { name: anim.name, samplers, channels };
  });

  // ---- Morph targets (POSITION displacements, one accessor per target).
  const morphNames = spec.morphTargetNames ?? [];
  const morphTargets = morphNames.map((_, k) =>
    ({ POSITION: floats([0, 0.01 * (k + 1), 0, 0, 0.01 * (k + 1), 0, 0, 0.01 * (k + 1), 0], 'VEC3', true) }),
  );

  // ---- Node table (+ mesh node appended last).
  const children = new Map<number, number[]>();
  spec.nodes.forEach((n, i) => {
    if (n.parent !== null) children.set(n.parent, [...(children.get(n.parent) ?? []), i]);
  });
  const nodes: object[] = spec.nodes.map((n, i) => ({
    name: n.name,
    ...(n.translation ? { translation: n.translation } : {}),
    ...(n.rotation ? { rotation: n.rotation } : {}),
    ...(n.scale ? { scale: n.scale } : {}),
    ...(children.has(i) ? { children: children.get(i) } : {}),
  }));
  const meshNodeIndex = nodes.length;
  nodes.push({ name: 'SkinnedTriangle', mesh: 0, skin: 0 });
  const rootIndices = spec.nodes.flatMap((n, i) => (n.parent === null ? [i] : []));

  // ---- Buffer layout: one 4-aligned bufferView per accessor.
  let offset = 0;
  const bufferViews: object[] = [];
  const accessors: object[] = [];
  const parts: Uint8Array[] = [];
  slots.forEach((slot, i) => {
    const padded = (offset + 3) & ~3;
    if (padded !== offset) parts.push(new Uint8Array(padded - offset));
    offset = padded;
    bufferViews.push({ buffer: 0, byteOffset: offset, byteLength: slot.bytes.length });
    accessors.push({
      bufferView: i,
      componentType: slot.componentType,
      count: slot.count,
      type: slot.type,
      ...(slot.min ? { min: slot.min, max: slot.max } : {}),
    });
    parts.push(slot.bytes);
    offset += slot.bytes.length;
  });
  const binLength = (offset + 3) & ~3;
  const bin = new Uint8Array(binLength);
  let cursor = 0;
  for (const part of parts) {
    bin.set(part, cursor);
    cursor += part.length;
  }

  const json = {
    asset: { version: '2.0', ...(spec.generator ? { generator: spec.generator } : {}) },
    ...(spec.extensionsUsed?.length ? { extensionsUsed: spec.extensionsUsed } : {}),
    scene: 0,
    scenes: [{ nodes: [...rootIndices, meshNodeIndex] }],
    nodes,
    meshes: [
      {
        primitives: [
          {
            attributes: { POSITION: positionAccessor, JOINTS_0: jointsAccessor, WEIGHTS_0: weightsAccessor },
            ...(spec.materials !== undefined && spec.materials.length > 0 ? { material: 0 } : {}),
            ...(morphTargets.length ? { targets: morphTargets } : {}),
          },
        ],
        ...(morphNames.length ? { extras: { targetNames: morphNames } } : {}),
      },
    ],
    ...(spec.materials !== undefined && spec.materials.length > 0
      ? {
          materials: spec.materials.map((name) => ({
            ...(name === null ? {} : { name }),
            pbrMetallicRoughness: { metallicFactor: 1, roughnessFactor: 1 },
          })),
        }
      : {}),
    skins: [{ joints: jointIndices, inverseBindMatrices: ibmAccessor }],
    ...(animations.length ? { animations } : {}),
    buffers: [{ byteLength: binLength }],
    bufferViews,
    accessors,
  };

  // ---- GLB container: header + JSON chunk (space-padded) + BIN chunk.
  const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
  const jsonPadded = (jsonBytes.length + 3) & ~3;
  const total = 12 + 8 + jsonPadded + 8 + binLength;
  const glb = new ArrayBuffer(total);
  const view = new DataView(glb);
  const out = new Uint8Array(glb);
  view.setUint32(0, 0x46546c67, true); // 'glTF'
  view.setUint32(4, 2, true);
  view.setUint32(8, total, true);
  view.setUint32(12, jsonPadded, true);
  view.setUint32(16, 0x4e4f534a, true); // 'JSON'
  out.set(jsonBytes, 20);
  out.fill(0x20, 20 + jsonBytes.length, 20 + jsonPadded);
  view.setUint32(20 + jsonPadded, binLength, true);
  view.setUint32(24 + jsonPadded, 0x004e4942, true); // 'BIN'
  out.set(bin, 28 + jsonPadded);
  return glb;
}
